import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { History, Undo2, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";
import { activityApi } from "../lib/api";
import { BulkUndoSection } from "./BulkUndoSection";
import { confirmDialog } from "../lib/confirm";
import { DropdownPortal } from "./DropdownPortal";

const zamanTR = (iso) => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const sn = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sn < 60) return "az önce";
  if (sn < 3600) return `${Math.floor(sn / 60)} dk önce`;
  if (sn < 86400) return `${Math.floor(sn / 3600)} sa önce`;
  return `${Math.floor(sn / 86400)} gün önce`;
};

const Satir = memo(({ row, busy, onUndo }) => {
  const kapali = row.undone || !row.undoable;
  return (
    <div
      data-testid={`undo-row-${row.id}`}
      className="flex items-start gap-2 px-2 py-1.5 border-b border-sertex-cyan/10 last:border-0 hover:bg-sertex-cyan/5 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-mono text-sertex-text leading-snug">{row.label}</div>
        <div className="hud-text text-sertex-textMuted mt-0.5">
          {row.actor_name} · {zamanTR(row.at)}
          {row.undone && <span className="ml-1 text-amber-400">· GERİ ALINDI</span>}
          {!row.undoable && !row.undone && (
            <span className="ml-1 text-rose-400">· GERİ ALINAMAZ</span>
          )}
        </div>
      </div>
      <button
        type="button"
        disabled={kapali || busy}
        onClick={() => onUndo(row)}
        data-testid={`undo-btn-${row.id}`}
        title={
          row.undone
            ? "Bu işlem zaten geri alındı"
            : row.undoable
              ? "Bu işlemi geri al"
              : row.irreversible_reason || "Bu işlem geri alınamaz"
        }
        className="shrink-0 px-1.5 py-0.5 rounded border hud-text transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-sertex-cyan/40 text-sertex-cyan hover:bg-sertex-cyan/10"
      >
        {kapali ? <Ban className="h-3 w-3" /> : <Undo2 className="h-3 w-3" />}
      </button>
    </div>
  );
});
Satir.displayName = "UndoSatir";

export const UndoMenu = ({ onUndone, light = false }) => {
  const [acik, setAcik] = useState(false);
  const btnRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [busy, setBusy] = useState(false);
  // FAZ F — çift gönderim kilidi (aynı işlemi iki kez geri alma riski).
  const undoKilit = useRef(false);
  // "Herkesin işlemleri" kapsamı yalnızca sahip/yöneticiye gösterilir.
  const [canSeeAll, setCanSeeAll] = useState(false);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const d = await activityApi.list(20);
      setRows(d.items || []);
      setCanSeeAll(!!d.can_see_all);
    } catch {
      toast.error("İşlem kaydı alınamadı");
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => { if (acik) yukle(); }, [acik, yukle]);

  const geriAl = useCallback(async (row) => {
    if (undoKilit.current) return;
    let onizleme = null;
    try {
      onizleme = await activityApi.preview(row.id);
    } catch {
      toast.error("İşlem bilgisi alınamadı");
      return;
    }
    const cakisma = onizleme.conflict;
    const mesaj = cakisma
      ? `${row.label}\n\nUYARI: ${cakisma}\n\nYine de geri alınsın mı?`
      : `${row.label}\n\nBu işlem geri alınacak. Onaylıyor musunuz?`;
    const ok = await confirmDialog({
      title: cakisma ? "Çakışma Uyarısı" : "İşlemi Geri Al",
      message: mesaj,
      confirmText: "Geri Al",
      danger: !!cakisma,
    });
    if (!ok) return;
    if (undoKilit.current) return;
    undoKilit.current = true;
    setBusy(true);
    try {
      await activityApi.undo(row.id, true);
      toast.success("İşlem geri alındı");
      await yukle();
      onUndone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri alma başarısız");
      await yukle();
    } finally {
      setBusy(false);
      undoKilit.current = false;
    }
  }, [yukle, onUndone]);

  return (
    <div className="relative">
      <button
        type="button"
        ref={btnRef}
        onClick={() => setAcik((v) => !v)}
        data-testid="undo-menu-btn"
        title="Son işlemler — geri alma menüsünü açar"
        className={`inline-flex items-center gap-1 px-2 py-1 rounded border hud-text transition-colors ${
          light
            ? "border-amber-600/50 text-amber-700 bg-amber-50 hover:bg-amber-100"
            : "border-amber-400/40 text-amber-300 hover:bg-amber-400/10"
        }`}
      >
        <History className="h-3 w-3" /> GERİ AL
      </button>

      {acik && (
        <DropdownPortal anchorRef={btnRef} width={320} onClose={() => setAcik(false)} testid="undo-menu-portal">
        <div
          data-testid="undo-menu-panel"
          className="glass-panel bg-sertex-bg border border-amber-400/40 rounded-lg shadow-2xl"
        >
            {/* FAZ E2 — zaman penceresiyle toptan geri alma (listenin ÜSTÜNDE) */}
            <BulkUndoSection canSeeAll={canSeeAll} onDone={() => { yukle(); onUndone?.(); }} />
            <div className="flex items-center justify-between px-2 py-1.5 border-b border-amber-400/20 sticky top-0 bg-sertex-bg/95">
              <span className="hud-text text-amber-300">SON 20 İŞLEM</span>
              <button
                type="button"
                onClick={yukle}
                data-testid="undo-refresh"
                className="hud-text text-sertex-textMuted hover:text-sertex-cyan transition-colors"
              >
                YENİLE
              </button>
            </div>
            {yukleniyor ? (
              <div className="flex items-center gap-2 px-2 py-3 hud-text text-sertex-textMuted">
                <Loader2 className="h-3 w-3 animate-spin" /> YÜKLENİYOR…
              </div>
            ) : rows.length === 0 ? (
              <div className="px-2 py-3 hud-text text-sertex-textMuted" data-testid="undo-empty">
                Henüz kayıtlı işlem yok.
              </div>
            ) : (
              <div data-testid="undo-list">
                {rows.map((r) => (
                  <Satir key={r.id} row={r} busy={busy} onUndo={geriAl} />
                ))}
              </div>
            )}
            <div className="px-2 py-1 border-t border-amber-400/20 hud-text text-sertex-textMuted/70">
              Kayıtlar 48 saat saklanır.
            </div>
        </div>
        </DropdownPortal>
      )}
    </div>
  );
};

export default UndoMenu;
